import React, { useState } from "react";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";
import colors from "../styles/colors";

interface ImageGalleryProps {
  images: string[];
  title: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, title }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const visibleCount = 5;
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const handlePrev = () => {
    setActiveImageIndex((prevIndex) => {
      const newIndex = prevIndex > 0 ? prevIndex - 1 : images.length - 1;
      updateVisibleThumbnails(newIndex);
      return newIndex;
    });
    setIsZoomed(false);
  };

  const handleNext = () => {
    setActiveImageIndex((prevIndex) => {
      const newIndex = prevIndex < images.length - 1 ? prevIndex + 1 : 0;
      updateVisibleThumbnails(newIndex);
      return newIndex;
    });
    setIsZoomed(false);
  };

  const updateVisibleThumbnails = (newIndex: number) => {
    // Ha az új index kívül esik a látható tartományon, frissítjük a kezdő indexet
    if (newIndex < visibleStartIndex) {
      setVisibleStartIndex(newIndex);
    } else if (newIndex >= visibleStartIndex + visibleCount) {
      setVisibleStartIndex(newIndex - visibleCount + 1);
    } else if (newIndex === 0) {
      // Ha az új index 0 (amikor újraindul a galéria), állítsuk vissza a thumbnail scrollt is
      setVisibleStartIndex(0);
    }
  };

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isZoomed) {
      setIsZoomed(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setZoomPosition({ x, y });
      setIsZoomed(true);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX === null) return;

    const touchEndX = e.touches[0].clientX;
    const touchDiff = touchStartX - touchEndX;

    if (touchDiff > 50) {
      handleNext(); // Jobbra csúsztatás → következő kép
      setTouchStartX(null);
    } else if (touchDiff < -50) {
      handlePrev(); // Balra csúsztatás → előző kép
      setTouchStartX(null);
    }
  };

  return (
    <div className="relative w-full font-serif min-h-[60vh] bg-black py-8">
      {/* title */}
      <h3
        className="text-center text-lg md:text-xl mt-4 mb-4 p-4"
        style={{ color: colors.lightCream }}
      >
        {title}
      </h3>
      {/* Bal gomb */}
      <button
        onClick={handlePrev}
        className="z-50 absolute left-2 md:left-4 lg:left-8 top-1/2 transform -translate-y-1/2 text-white text-2xl md:text-4xl font-bold hover:text-gray-300"
      >
        <SlArrowLeft />
      </button>
      {/* Jobb gomb */}
      <button
        onClick={handleNext}
        className="z-50 absolute right-2 md:right-4 lg:right-8 top-1/2 transform -translate-y-1/2 text-white text-2xl md:text-4xl font-bold hover:text-gray-300"
      >
        <SlArrowRight />
      </button>
      <div className="flex flex-col items-center gap-4 w-full max-h-[80vh] max-w-screen-xl mx-auto px-2 md:px-6">
        {/* Nagy kép konténer */}
        <div className="relative flex flex-col items-center w-full h-[60vh] md:h-[70vh] bg-black rounded-lg overflow-hidden">
          {/* Kép */}
          <div className="relative w-full h-full flex justify-center items-center overflow-hidden">
            <img
              onClick={handleImageClick}
              loading="lazy"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              src={images[activeImageIndex]}
              alt="Selected"
              className={`w-auto h-full max-w-full max-h-full object-contain transition-transform duration-300 cursor-zoom-in ${
                isZoomed ? "cursor-zoom-out" : "cursor-zoom-in"
              }`}
              style={
                isZoomed
                  ? {
                      transform: `scale(1.75)`,
                      transformOrigin: `${zoomPosition.x}px ${zoomPosition.y}px`,
                    }
                  : undefined
              }
            />
          </div>
        </div>

        {/* Thumbnail sáv */}
        <div className="flex justify-center w-full">
          <div className="flex gap-2 md:gap-4 overflow-x-auto py-2">
            {images
              .slice(visibleStartIndex, visibleStartIndex + visibleCount)
              .map((image, index) => {
                const realIndex = visibleStartIndex + index;
                return (
                  <div
                    key={realIndex}
                    className={`flex-shrink-0 w-14 h-14 md:w-20 md:h-20 cursor-pointer rounded-lg overflow-hidden border-2 ${
                      realIndex === activeImageIndex
                        ? "border-primary"
                        : "border-transparent"
                    }`}
                    onClick={() => {
                      setActiveImageIndex(realIndex);
                      setIsZoomed(false);
                    }}
                  >
                    <img
                      src={image}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      alt={`Gallery thumbnail ${realIndex + 1}`}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageGallery;
